import {
  Classes,
  Colors,
  Drawer,
  NonIdealState,
  Position,
} from "@blueprintjs/core";
import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import "@blueprintjs/icons/lib/css/blueprint-icons.css";
import "@blueprintjs/popover2/lib/css/blueprint-popover2.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "filepond/dist/filepond.min.css";
import "normalize.css/normalize.css";
import { Footer, useLocalStorage } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { BaseAppNavigation } from "./app-navigation";
import { AppRouter } from "./AppRouter";
import {
  DialogRoot,
  FullPageLoader,
  Header,
  NAVIGATION_MENU_WIDTH,
  NavigationMenu,
} from "./components";
import { AppError } from "./components/AppError";
import { useCommonHooks, useTl } from "./hooks";
import { ETLCodes } from "./locales";

const AppContainer = styled.div<{ isdark?: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  flex-direction: column;
  background-color: ${(props) =>
    props.isdark ? Colors.DARK_GRAY2 : Colors.LIGHT_GRAY3};
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  flex-wrap: wrap;
`;

const FooterContainer = styled.div``;

export interface IAppProps {}

export const App: React.FunctionComponent<IAppProps> = () => {
  const [menuFixed, setMenuFixed] = useLocalStorage("menuFixed", true);

  const [drawerVisible, setDrawerVisible] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(false);

  const {
    globalDataLoading,
    appVersion,
    backendNotReachable,
    authLoading,
    authError,
    isLoggedIn,
  } = useCommonHooks();

  const appIsLoading = React.useMemo(
    () => globalDataLoading || authLoading,
    [globalDataLoading, authLoading]
  );

  const { t } = useTl();

  if (appIsLoading || !isLoggedIn) {
    return <FullPageLoader />;
  }

  return (
    <AppContainer>
      <AppError>
        <Header
          showMenuIcon={!menuFixed}
          toggleLeftMenu={() => {
            setDrawerVisible((s) => !s);
          }}
          isDark={darkMode}
          onDarkSwitchChange={() => setDarkMode(!darkMode)}
        />
        <ContentContainer className="page">
          {backendNotReachable ? (
            <NonIdealState
              icon="th-disconnect"
              title={"Backend not reachable"}
            />
          ) : authError || !isLoggedIn ? (
            <NonIdealState
              icon="error"
              title={t(ETLCodes.NotAuthorized)}
              description={t(ETLCodes.UserNotAuthorizedMessage)}
            />
          ) : (
            <>
              <Drawer
                hasBackdrop={true}
                className={Classes.DARK}
                isOpen={drawerVisible}
                position={Position.LEFT}
                size={NAVIGATION_MENU_WIDTH}
                onClose={() => setDrawerVisible(false)}
                canEscapeKeyClose={true}
                canOutsideClickClose={true}
                usePortal={false}
              >
                <div className={Classes.DRAWER_BODY}>
                  <NavigationMenu
                    collapseOtherOnSelection={true}
                    maxAutoCollapseLevel={0}
                    navigationMenu={BaseAppNavigation}
                    isFixed={menuFixed}
                    changeFixed={() => {
                      if (!menuFixed) {
                        setDrawerVisible(false);
                      }
                      setMenuFixed(!menuFixed);
                    }}
                    onNavigation={() => setDrawerVisible(false)}
                  />
                </div>
                <div className={Classes.DRAWER_FOOTER} />
              </Drawer>
              {menuFixed && (
                <NavigationMenu
                  navigationMenu={BaseAppNavigation}
                  collapseOtherOnSelection={true}
                  maxAutoCollapseLevel={0}
                  isFixed={menuFixed}
                  changeFixed={() => setMenuFixed(!menuFixed)}
                />
              )}
              <AppRouter />
            </>
          )}
        </ContentContainer>
        <FooterContainer>
          <Footer versionLoading={globalDataLoading} version={appVersion} />
        </FooterContainer>
        <DialogRoot />
      </AppError>
    </AppContainer>
  );
};
